import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { supabase } from './supabase'
import { clearLocalData, localDb, putCache, readCacheOne } from './db'
import { initSyncListeners } from './sync'
import { primeMasterData } from './data'
import type { Profile, Site } from './types'

interface AuthValue {
  profile: Profile | null
  site: Site | null
  loading: boolean
  priming: boolean
  primeError: string | null
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  reprime: () => Promise<void>
}

const Ctx = createContext<AuthValue>(null as unknown as AuthValue)
export const useAuth = () => useContext(Ctx)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [site, setSite] = useState<Site | null>(null)
  const [loading, setLoading] = useState(true)
  const [priming, setPriming] = useState(false)
  const [primeError, setPrimeError] = useState<string | null>(null)

  async function loadProfile(userId: string) {
    // Offline sign-in: the profile cached at the last successful login is enough
    // to render the app; writes still queue and the RLS check happens on sync.
    if (!navigator.onLine) {
      const cached = await localDb.meta.get('current_profile')
      if (cached) {
        const p = cached.value as Profile
        setProfile(p)
        if (p.site_id) setSite((await readCacheOne<Site>('sites', p.site_id)) ?? null)
        return
      }
    }
    const { data, error } = await supabase
      .from('profiles').select('*').eq('id', userId).single()
    if (error || !data) {
      throw new Error(error?.message ?? 'This login has no profile. Ask an administrator to set one up.')
    }
    if (!data.is_active) throw new Error('This account has been deactivated.')

    setProfile(data as Profile)
    await localDb.meta.put({ key: 'current_profile', value: data })

    if (data.site_id) {
      const { data: s } = await supabase.from('sites').select('*').eq('id', data.site_id).maybeSingle()
      if (s) { setSite(s as Site); await putCache('sites', [s]) }
    }
  }

  async function prime() {
    setPriming(true)
    setPrimeError(null)
    const r = await primeMasterData()
    if (!r.ok) setPrimeError(r.error ?? 'Could not download master data')
    setPriming(false)
  }

  useEffect(() => {
    const cleanup = initSyncListeners()

    supabase.auth.getSession().then(async ({ data }) => {
      if (data.session?.user) {
        try {
          await loadProfile(data.session.user.id)
          if (navigator.onLine) void prime()
        } catch (e) {
          console.error(e)
          await supabase.auth.signOut({ scope: 'local' })
        }
      }
      setLoading(false)
    })

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') { setProfile(null); setSite(null); return }
      if (session?.user && !profile) {
        try { await loadProfile(session.user.id) } catch (e) { console.error(e) }
      }
    })

    return () => { sub.subscription.unsubscribe(); cleanup?.() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value: AuthValue = {
    profile,
    site,
    loading,
    priming,
    primeError,
    async signIn(email, password) {
      const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
      if (error) throw new Error(friendlyAuthError(error.message))
      await loadProfile(data.user.id)
      void prime()
    },
    async signOut() {
      // 'local' = end THIS login only. The default ('global') ended every login
      // of the account on every computer and phone; those kept showing data but
      // every export / PDF then failed with "Not authenticated" (11-09-2026).
      await supabase.auth.signOut({ scope: 'local' })
      await clearLocalData()
      setProfile(null)
      setSite(null)
    },
    reprime: prime,
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

function friendlyAuthError(msg: string): string {
  if (/invalid login credentials/i.test(msg)) {
    return 'Wrong email or password. Check the caps-lock key and try again.'
  }
  if (/failed to fetch|network/i.test(msg)) {
    return 'Cannot reach the server. Check that the Supabase stack is running (supabase start) and that VITE_SUPABASE_URL in .env.local is correct.'
  }
  return msg
}
